import React, { useCallback, useState } from 'react';
import GuideDialog, { GuideSlide } from '@/components/guide/GuideDialog';
import type { ExceptionSettlementMode } from '@/features/coachCommissionTable/types';

/**
 * 예외 수업료 안내. 예외 수업료 창을 처음 열 때 한 번 뜨고, 「예외 수업료란?」으로 언제든 다시 연다.
 * 한 번 닫으면 이 컴퓨터에서는 자동으로 다시 뜨지 않는다(창을 자주 여는 화면이라 매번 뜨면 방해된다).
 * 저장소 접근이 막혀도 화면은 깨지지 않는다 — 실패하면 「안 본 것」으로 본다.
 */
const SEEN_KEY = 'rappo:exceptionRates:guide:202610:seen';

const readSeen = () => {
    try {
        return window.localStorage.getItem(SEEN_KEY) === '1';
    } catch {
        return false;
    }
};
const writeSeen = () => {
    try {
        window.localStorage.setItem(SEEN_KEY, '1');
    } catch {
        // 저장이 안 되면 다음에 한 번 더 뜰 뿐이다.
    }
};

export const useExceptionRatesGuide = () => {
    const [open, setOpen] = useState(false);
    // 창이 처음 열릴 때 부른다 — 아직 안 봤으면 띄운다.
    const showOnce = useCallback(() => {
        if (!readSeen()) setOpen(true);
    }, []);
    const close = useCallback(() => {
        writeSeen();
        setOpen(false);
    }, []);
    return { open, show: () => setOpen(true), showOnce, close };
};

const Example = ({ who, rule, note }: { who: string; rule: string; note: string }) => (
    <div className="flex items-start gap-[14px] rounded-[14px] bg-white border border-solid border-[#E5E8EB] px-[16px] py-[12px]">
        <span className="shrink-0 w-[120px] text-[14px] leading-[22px] font-semibold text-[#333D4B]">{who}</span>
        <span className="text-[14px] leading-[22px] text-[#333D4B] break-keep">
            {rule}
            <span className="block text-[13px] text-[#6B7280]">{note}</span>
        </span>
    </div>
);

const Step = ({ n, title, text }: { n: number; title: string; text: string }) => (
    <li className="flex items-start gap-[12px] rounded-[14px] bg-white border border-solid border-[#E5E8EB] px-[16px] py-[12px]">
        <span className="shrink-0 w-[26px] h-[26px] rounded-full bg-[#E8F0FF] text-[#1B4FD8] text-[13px] font-semibold flex items-center justify-center">{n}</span>
        <span className="text-[14px] leading-[22px] text-[#333D4B] break-keep">
            <span className="font-semibold">{title}</span>
            <span className="block text-[#4E5968]">{text}</span>
        </span>
    </li>
);

/**
 * 정산 방식마다 안내가 다르다.
 * - 상세·세션별 정산: 구간표 기준, 재등록 수강권 예외를 바로 고를 수 있다.
 * - 기본 정산: 정산 비율 기준, 재등록 수강권 전체 예외는 쓰지 않고(분류만), 고정·요율은 재등록·노쇼 값을 따로 정할 수 있다.
 */
const buildSlides = (mode: ExceptionSettlementMode): GuideSlide[] => {
    const isBase = mode === 'base';
    const basis = isBase ? '정산 비율' : '구간표';
    return [
    {
        title: '예외 수업료는 이럴 때 써요',
        body: `수업료는 보통 ${isBase ? '강사의 정산 비율' : '위의 구간표'}대로 계산해요. 그런데 어떤 수강권만 수업료를 다르게 주고 싶을 때가 있어요. 그때 강사마다 예외를 정해 두는 거예요.`,
        content: (
            <div className="flex flex-col gap-[10px]">
                <Example who="공구 PT 상품" rule="1회 30,000원으로 고정" note={`${basis}와 상관없이 수업 1회마다 정한 금액을 줘요.`} />
                {isBase ? (
                    <Example
                        who="바디프로필 상품"
                        rule="1회 판매가의 40%"
                        note="재등록·노쇼 수업은 값을 따로 정할 수 있어요. 비우면 신규·출석 값을 써요."
                    />
                ) : (
                    <Example who="재등록 수강권" rule="구간 수업료의 1.2배" note="구간표로 나온 수업료에 정한 배수를 곱해요." />
                )}
                <p className="text-[13px] leading-[20px] text-[#6B7280]">{`예외에 해당하지 않는 수업은 지금처럼 ${basis}대로 계산해요.`}</p>
            </div>
        ),
    },
    {
        title: '이 순서로 준비해요',
        body: isBase
            ? '특정 상품만 다르게 하려면 그 상품에 「정산 분류」를 먼저 달아 주세요.'
            : '재등록은 바로 고를 수 있어요. 특정 상품만 다르게 하려면 그 상품에 「정산 분류」를 먼저 달아 주세요.',
        content: (
            <ol className="flex flex-col gap-[10px]">
                <Step n={1} title="정산 분류 만들기" text="예외 수업료 창의 「분류 관리」에서 분류를 만들어요. 예) 공구 PT" />
                <Step n={2} title="상품에 분류 달기" text="수강권 관리에서 상품을 열고 「정산 분류」를 골라요. 이미 판매한 수강권에도 적용할지는 저장할 때 물어봐요." />
                <Step
                    n={3}
                    title="강사마다 예외 정하기"
                    text={
                        isBase
                            ? '예외 수업료 창에서 분류와 계산 방식, 값을 넣고 저장해요.'
                            : '예외 수업료 창에서 분류(또는 재등록)와 계산 방식, 값을 넣고 저장해요.'
                    }
                />
            </ol>
        ),
    },
    {
        title: '이것만 알아 두세요',
        body: '저장하면 바로 급여 계산에 반영돼요.',
        content: (
            <ul className="flex flex-col gap-[10px]">
                <Example who="적용 시작" rule="고른 달부터 적용돼요" note="다음 달에 따로 정하지 않으면 그대로 이어져요." />
                <Example who="지난 달로 저장" rule="그 달부터 급여가 다시 계산돼요" note="저장 전에 한 번 더 확인을 받아요." />
                <Example who="다른 강사에게" rule="같은 규칙을 복사할 수 있어요" note="예외 수업료 창의 「다른 강사에게 같은 예외 적용」을 누르면 돼요." />
            </ul>
        ),
    },
    ];
};

const ExceptionRatesGuide = ({
    onClose,
    settlementMode = 'tier',
}: {
    onClose: () => void;
    /** 생략하면 상세 정산 안내(예전 그대로). */
    settlementMode?: ExceptionSettlementMode;
}) => (
    // 요율 창의 작은 대화상자(z 99998) 위에 떠야 한다.
    <GuideDialog
        badge="예외 수업료 안내"
        slides={buildSlides(settlementMode)}
        onClose={onClose}
        minHeight={380}
        zIndex={99999}
        showNever={false}
    />
);

export default ExceptionRatesGuide;
